import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import { Laptop, Public, SettingsEthernet, SyncProblem } from '@material-ui/icons';
import { Handle, Position } from 'reactflow';
import compact from 'lodash/compact';

import { useStyles } from './NodeStyles';
import { NPoint2K, NPoint3K, NPoint4K, NPoint4KW, NPoint3KG } from '../icon';
import { HopNodeTooltip } from './HopNodeTooltip';
import useIsPrinting from '../../utilities/PrintUtilities';

/**
 * Gets the correct agent icon using API agent data
 * @param {Object} agentData
 * @returns agent icon
 */
function getAgentIcon(agentData) {
    switch (agentData.typeIcon) {
        case 'ipm-icons-agent2k':
            return <NPoint2K />;
        case 'ipm-icons-agent3k':
            return <NPoint3K />;
        case 'ipm-icons-agent4k':
            return <NPoint4K />;
        case 'ipm-icons-agent4kw':
            return <NPoint4KW />;
        case 'ipm-icons-agent3kg':
            return <NPoint3KG />;
        default:
            return <Laptop />;
    }
}

/**
 * Constructs location string from hop location data
 * @param {Object} hopLocationData API location data for a hop
 * @returns hop location string
 */
function getHopLocationString(hopLocationData) {
    return compact([hopLocationData.city, hopLocationData.subdivision || hopLocationData.subdivisionIsoCode]).join(
        ', '
    );
}

export default function HopNode({ data }) {
    const styles = useStyles();
    const isPrinting = useIsPrinting();

    return (
        <Box className={styles.nodeWrapper}>
            <Box className={styles.hopNodeTopContent}>
                {!data.isAgent && (
                    <Typography variant="body2">
                        {data.hopData.avgResponseTime < 1 ? '<1' : data.hopData.avgResponseTime}ms
                    </Typography>
                )}
                {data.isDuplicate && <SyncProblem fontSize="large" />}
            </Box>
            <HopNodeTooltip nodeData={data}>
                <Box
                    className={`${styles.node} ${styles.hopNode}`}
                    sx={{ boxShadow: data.isDuplicate && !isPrinting ? '0 0 16px #eb7861' : '' }}
                >
                    {data.isIspHop && <SettingsEthernet />}
                    {data.isTarget && <Public />}
                    {data.isAgent && getAgentIcon(data.agentData)}
                    <Handle type="source" position={Position.Right} isConnectable={false} />
                    <Handle type="target" position={Position.Left} isConnectable={false} />
                </Box>
            </HopNodeTooltip>
            {(data.isAgent || data.isTarget || data.isIspHop) && (
                <Box className={`${styles.hopNodeBottomContent} hop-node-bottom-content`}>
                    {data.isAgent && (
                        <Typography variant="body1" className={styles.infoText}>
                            {data.agentData.name}
                        </Typography>
                    )}
                    {data.isTarget && (
                        <Typography variant="body1" className={styles.infoText}>
                            {data.testData.name}
                        </Typography>
                    )}
                    {(data.isIspHop || data.isAgent) && (
                        <>
                            <Typography variant="body1" className={styles.infoText}>
                                {data.isIspHop && (data.hopData.provider || data.hopData.location?.provider)}
                            </Typography>
                            {data.hopData.location && (
                                <>
                                    <Typography variant="body2" className={styles.infoText}>
                                        {getHopLocationString(data.hopData.location)}
                                    </Typography>
                                    {(data.hopData.location.country || data.hopData.location.isoCode) && (
                                        <Typography variant="body2" className={styles.infoText}>
                                            {data.hopData.location.country || data.hopData.location.isoCode}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}

HopNode.propTypes = {
    // The data prop is passed through to the component by Reactflow
    data: PropTypes.object,
};

HopNode.defaultProps = {
    data: {},
};
